import {
  Connection,
  ConnectionAdmissionRef,
  EnvironmentRegistry,
  connectionAdmission,
} from "@t3tools/client-runtime/connection";
import { RecentEnvironmentDescriptorsRef } from "@t3tools/client-runtime/environment";
import { shellSnapshotLoaderLayer } from "@t3tools/client-runtime/state/shell";
import { mateDiagnostics } from "@t3tools/client-runtime/zerops/diagnostics";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";
import { Atom } from "effect/reactivity";
import type { EnvironmentId } from "@t3tools/contracts";

import { runtimeContextLayer } from "../lib/runtime";
import {
  backgroundActivityObserverLayer,
  backgroundActivityReporterLayer,
} from "../lib/backgroundActivityReporter";
import { mateDescriptors } from "../zerops/mateDescriptors";
import { connectionPlatformLayer } from "./platform";

const providedConnectionPlatformLayer = connectionPlatformLayer.pipe(
  Layer.provide(runtimeContextLayer),
);

const snapshotLoaderLayer = shellSnapshotLoaderLayer;

type ConnectionLayerSource =
  | typeof Connection.layer
  | typeof snapshotLoaderLayer
  | typeof runtimeContextLayer
  | typeof connectionPlatformLayer
  | typeof backgroundActivityObserverLayer
  | typeof backgroundActivityReporterLayer;

/** A hidden tab's timers run once a minute, so its sockets wait for the person before judging pongs. */
const pageHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

const providedClientConnectionLayer = Layer.merge(
  Connection.layerWithOptions({
    usageLimitSources: true,
    usageLimitsCommand: true,
    pingTimeoutPaused: pageHidden,
  }),
  snapshotLoaderLayer,
).pipe(
  Layer.provideMerge(
    Layer.mergeAll(
      runtimeContextLayer,
      providedConnectionPlatformLayer,
      backgroundActivityObserverLayer,
      // The browser opens one socket at a time per address: the tab's sockets take turns.
      Layer.succeed(ConnectionAdmissionRef, connectionAdmission),
      // A Mate's connect presents the descriptor its door just read, never reading it again.
      Layer.succeed(RecentEnvironmentDescriptorsRef, { recent: mateDescriptors.recent }),
    ),
  ),
);

/**
 * The catalog's lifetime, for diagnostics: this runtime built, each
 * environment it gains or loses, and the runtime disposed.
 */
const catalogDiagnosticsLayer = Layer.effectDiscard(
  Effect.gen(function* () {
    const registry = yield* EnvironmentRegistry;
    yield* Effect.acquireRelease(
      Effect.sync(() => mateDiagnostics.record({ kind: "catalog", change: "built" })),
      () => Effect.sync(() => mateDiagnostics.record({ kind: "catalog", change: "disposed" })),
    );
    let known: ReadonlySet<EnvironmentId> = new Set();
    yield* SubscriptionRef.changes(registry.entries).pipe(
      Stream.runForEach((entries) =>
        Effect.sync(() => {
          for (const environmentId of entries.keys()) {
            if (!known.has(environmentId))
              mateDiagnostics.record({ kind: "catalog", change: "added", environmentId });
          }
          for (const environmentId of known) {
            if (!entries.has(environmentId))
              mateDiagnostics.record({ kind: "catalog", change: "removed", environmentId });
          }
          known = new Set(entries.keys());
        }),
      ),
      Effect.forkScoped,
    );
  }),
);

const connectionLayer = Layer.mergeAll(
  backgroundActivityReporterLayer,
  catalogDiagnosticsLayer,
).pipe(Layer.provideMerge(providedClientConnectionLayer));

/**
 * The T3 connection runtime lives for the account, not for whichever views
 * read it: a page with no conversation on it never disposes the catalog. The
 * account's close disposes it with the atom registry (DESIGN §5 L4).
 */
export const connectionAtomRuntime: Atom.AtomRuntime<
  Layer.Success<ConnectionLayerSource>,
  Layer.Error<ConnectionLayerSource>
> = Atom.keepAlive(Atom.runtime(connectionLayer));
